import { AllegatoDtoTipoDocumentoEnum, ClienteDto, DatiAzienda } from "@api/client";
import { companyCheckAteco, companyUpdateCompany } from "common/_redux/company/actions";
import { companyDetailsSelector } from "common/_redux/company/selectors";
import { fileUpload } from "common/_redux/file/actions";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import BftDropzone from "common/components/dropzone";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import MessageModal from "common/components/message-modal";
import BftSelect from "common/components/select";
import { FilesUtils } from "common/file.utils";
import { Formik, datiAziendaSchema } from "common/validators/validators";
import { BooleanValues, IdTipoDocumenti, IdTipoDocumentiEstesi } from "constants/global";
import { PersonalitaGiuridica } from "constants/options";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { FileDocument } from "models/files";
import React, { ChangeEvent, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

const { TRUE, FALSE } = BooleanValues;

const personalitaGiuridica = PersonalitaGiuridica;

interface InitialValues extends DatiAzienda {
	SCO_TGI: FileDocument | undefined;
	// SCO_LAA: FileDocument | undefined;
	// SCO_DCM: FileDocument | undefined;
	ASS_ATC: FileDocument | undefined;
	ASS_VRL: FileDocument | undefined;
	ASS_API: FileDocument | undefined;
	LIC_AUT: FileDocument | undefined;
}

interface Props {
	onComplete: () => void;
}

const DatiAziendali: React.FC<Props> = (props) => {
	const { onComplete } = props;
	const dispatch = useAppDispatch();

	const [showModal, setShowModal] = useState<boolean>(false);

	const companyDetails = useAppSelector(companyDetailsSelector);

	const _handleSubmit = (values: InitialValues) => {
		let _values = { ...values };

		let documents: (FileDocument | undefined)[] = [];

		if (values?.centroScommesse) documents = [_values?.SCO_TGI];
		//  _values?.SCO_LAA, _values?.SCO_DCM];

		if (values?.associazione) documents = [_values?.ASS_ATC, _values?.ASS_VRL, _values?.ASS_API];

		if (values.codiceAteco === "95.25" || values.codiceAteco === "66.19")
			documents = [_values.LIC_AUT];

		documents = documents?.filter((f) => f != undefined && f?.size > 0);

		delete _values?.SCO_TGI;
		// delete _values?.SCO_LAA;
		// delete _values?.SCO_DCM;
		delete _values?.ASS_ATC;
		delete _values?.ASS_VRL;
		delete _values?.ASS_API;
		delete _values?.LIC_AUT;

		let request: ClienteDto = {
			...companyDetails,
			datiAzienda: _values,
		};

		dispatch(fileUpload(documents as FileDocument[]))
			.unwrap()
			.then((response) => {
				if (response || documents.length < 1) {
					dispatch(companyUpdateCompany(request))
						.unwrap()
						.then((res) => {
							if (res) {
								onComplete();
							}
						});
				}
			});
	};

	const handleOpenModal = () => {
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
	};

	return (
		<div>
			<Formik
				validationSchema={datiAziendaSchema}
				onSubmit={_handleSubmit}
				initialValues={
					{
						denominazione: companyDetails?.datiAzienda?.denominazione || "",
						naturaGiuridica: companyDetails?.datiAzienda?.naturaGiuridica,
						insegna: companyDetails?.datiAzienda?.insegna || "",
						rea: companyDetails?.datiAzienda?.rea || "",
						codiceAteco: companyDetails?.datiAzienda?.codiceAteco || "",
						descrizioneAtecoCerved: companyDetails?.datiAzienda?.descrizioneAtecoCerved || "",
						codiceSdi: companyDetails?.datiAzienda?.codiceSdi || "",
						codiceSae: companyDetails?.datiAzienda?.codiceSae || null,
						descrizione: companyDetails?.datiAzienda?.descrizione || "",
						centroScommesse:
							companyDetails?.datiAzienda?.centroScommesse ||
							companyDetails?.datiAzienda?.codiceAteco?.startsWith("92") ||
							false,
						associazione:
							companyDetails?.datiAzienda?.associazione ||
							companyDetails?.datiAzienda?.codiceSae === "500" ||
							companyDetails?.datiAzienda?.codiceSae === "501" ||
							companyDetails?.datiAzienda?.naturaGiuridica === "Associazione" ||
							companyDetails?.datiAzienda?.naturaGiuridica === "Associazione In Partecipazione" ||
							false,
						tipoAzienda: !!companyDetails?.infoAzienda?.partitaIva ? "PIVA" : "CF",
						SCO_TGI: FilesUtils.AllegatoDtoToFileDocument(
							companyDetails?.elencoAllegati?.find(
								(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.TulpsGioco
							)
						),
						// SCO_LAA: FilesUtils.AllegatoDtoToFileDocument(
						// 	companyDetails?.elencoAllegati?.find(
						// 		(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.LicenzaAams
						// 	)
						// ),
						// SCO_DCM: FilesUtils.AllegatoDtoToFileDocument(
						// 	companyDetails?.elencoAllegati?.find(
						// 		(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.DelegaCasaMadre
						// 	)
						// ),
						ASS_ATC: FilesUtils.AllegatoDtoToFileDocument(
							companyDetails?.elencoAllegati?.find(
								(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.AttoCostitutivo
							)
						),
						ASS_VRL: FilesUtils.AllegatoDtoToFileDocument(
							companyDetails?.elencoAllegati?.find(
								(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.VerbaleRappLegale
							)
						),
						ASS_API: FilesUtils.AllegatoDtoToFileDocument(
							companyDetails?.elencoAllegati?.find(
								(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.AttribuzionePartitaIva
							)
						),
						LIC_AUT: FilesUtils.AllegatoDtoToFileDocument(
							companyDetails?.elencoAllegati?.find(
								(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.LicAut
							)
						),
					} as InitialValues
				}
				enableReinitialize
			>
				{({
					handleSubmit,
					handleChange,
					setFieldValue,
					setFieldTouched,
					values,
					touched,
					errors,
				}) => {
					const handleChangeCustom = (input: string) => (e: any) => {
						handleChange(e);
						setFieldValue(input, e);

						if (input == "centroScommesse" && e == TRUE) {
							setFieldValue("associazione", false);
						} else if (input == "associazione" && e == TRUE) {
							setFieldValue("centroScommesse", false);
						} else {
							setFieldValue("centroScommesse", false);
							setFieldValue("associazione", false);
						}
					};

					const handleChangeCodiceAteco = (event: ChangeEvent<HTMLInputElement>) => {
						let inputValue = event.target.value.replace(/[^0-9]/g, "");
						inputValue = inputValue.replace(/(\d{2})(?=\d)/g, "$1.");

						const nuovoCodiceAteco = inputValue;
						const centroScommesseValue = nuovoCodiceAteco.startsWith("92");

						setFieldValue("centroScommesse", centroScommesseValue);

						// if (!centroScommesseValue) {
						// 	setFieldValue("associazione", false);
						// }

						if (values.codiceAteco !== inputValue) {
							setFieldValue("descrizione", "");
						}

						event.target.value = inputValue.substring(0, 8);
						handleChange(event);
					};

					const onBlurCheckAteco = (codiceAteco: string) => {
						dispatch(companyCheckAteco(codiceAteco))
							.unwrap()
							.then((response) => {
								if (response?.descrizioneAteco) {
									// console.log(response.descrizioneAteco);
									setFieldValue("descrizione", response?.descrizioneAteco);
								}
							});
					};
					const handleFileChange = (name: string, file: FileDocument | null) => {
						setFieldTouched(name);
						setFieldValue(name, file);
					};

					const formatCodiceAteco = (inputValue: string | undefined) => {
						if (inputValue) {
							inputValue = inputValue.replace(/\./g, "");

							inputValue =
								inputValue
									.match(/.{1,2}/g)
									?.map((substr, index) => (index === 0 ? substr : "." + substr))
									.join("") || "";

							return inputValue;
						}
					};

					return (
						<form noValidate onSubmit={handleSubmit}>
							<BftInput
								required
								type={"text"}
								name={"denominazione"}
								placeholder={"Denominazione/Ragione sociale*"}
								value={values.denominazione?.toUpperCase()}
								onChange={(e) => {
									const { name, value } = e.target;
									setFieldValue(name, value.toUpperCase());
								}}
								isInvalid={!!errors.denominazione && touched.denominazione}
							/>
							{errors.denominazione && touched.denominazione && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors.denominazione}
								</Form.Control.Feedback>
							)}

							{!companyDetails?.datiAzienda?.naturaGiuridica && (
								<BftSelect
									name={"naturaGiuridica"}
									placeholder={"Personalità Giuridica"}
									value={values.naturaGiuridica}
									onChange={(e) => {
										handleChange(e);
										const isAssociazione =
											["Associazione", "Associazione In Partecipazione"].includes(e.target.value) ||
											values.codiceSae === "500" ||
											values.codiceSae === "501";
										if (isAssociazione) {
											setFieldValue("associazione", true);
										} else {
											setFieldValue("associazione", false);
										}
									}}
									isInvalid={!!errors?.naturaGiuridica && touched?.naturaGiuridica}
									options={personalitaGiuridica}
								/>
							)}
							{errors.naturaGiuridica && touched.naturaGiuridica && (
								<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
									{errors.naturaGiuridica}
								</Form.Control.Feedback>
							)}

							<Row>
								<Col md={6}>
									<div className="d-flex align-items-center">
										<BftInput
											required
											type={"text"}
											className="col-11"
											name={"insegna"}
											placeholder={"Insegna"}
											value={values.insegna?.toUpperCase()}
											onChange={(e) => {
												const { name, value } = e.target;
												setFieldValue(name, value.toUpperCase());
											}}
											isInvalid={!!errors.insegna && touched.insegna}
										/>
										<BftInfoTooltip message="Nome commerciale dell'attività" />
									</div>
									{errors.insegna && touched.insegna && (
										<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
											{errors.insegna}
										</Form.Control.Feedback>
									)}
								</Col>
								<Col md={6}>
									<div className="d-flex align-items-center">
										<BftInput
											required
											className="col-11"
											type={"text"}
											name={"rea"}
											placeholder={"N°REA"}
											value={values.rea}
											onChange={(e) => {
												const { name, value } = e.target;
												setFieldValue(name, value.toUpperCase());
											}}
											isInvalid={!!errors.rea && touched.rea}
										/>
										<BftInfoTooltip message="Numero composto da 6 cifre, preceduto dalla sigla della provincia (es: MI 123456)" />
									</div>
									{errors.rea && touched.rea && (
										<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
											{errors.rea}
										</Form.Control.Feedback>
									)}
								</Col>
							</Row>

							<Row>
								<Col md={6}>
									<div className="d-flex align-items-center">
										<BftInput
											required
											className="col-11"
											type={"text"}
											name={"codiceAteco"}
											placeholder={
												"Codice ATECO" +
												(companyDetails?.infoAzienda?.partitaIva ? "*" : "") +
												(companyDetails?.infoAzienda?.tipoCliente === "PersonaFisica"
													? " / Tipo Attività"
													: "")
											}
											value={formatCodiceAteco(values.codiceAteco)}
											onChange={handleChangeCodiceAteco}
											onBlur={() => {
												if (values.codiceAteco) {
													onBlurCheckAteco(values.codiceAteco);
												}
											}}
											isInvalid={!!errors.codiceAteco && touched.codiceAteco}
										/>
										<BftInfoTooltip
											onClick={handleOpenModal}
											message={
												<span className="text-white">
													<i className="">
														<u className="text-white">
															Clicca la "i" per leggere il contenuto del messaggio!
														</u>
													</i>
												</span>
											}
										/>
									</div>
									{errors.codiceAteco && touched.codiceAteco && (
										<Form.Control.Feedback className="ms-3  mb-3" type="invalid">
											{errors.codiceAteco}
										</Form.Control.Feedback>
									)}
								</Col>

								<Col md={6}>
									<div className="d-flex align-items-center">
										<BftInput
											required
											className="col-11"
											type={"text"}
											name={"codiceSdi"}
											placeholder={"Codice SDI"}
											value={values.codiceSdi}
											onChange={(e) => {
												const { name, value } = e.target;
												setFieldValue(name, value.toUpperCase());
											}}
											isInvalid={!!errors.codiceSdi && touched.codiceSdi}
										/>
										<BftInfoTooltip message="Codice destinatario legato alla fatturazione elettronica (es. BA6ET11)" />
									</div>
									{errors.codiceSdi && touched.codiceSdi && (
										<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
											{errors.codiceSdi}
										</Form.Control.Feedback>
									)}
								</Col>
							</Row>

							<BftInput
								required
								type={"text"}
								as={"textarea"}
								inputStyle={{ minHeight: "100px" }}
								name={"descrizione"}
								placeholder={"Breve descrizione attività svolta*"}
								value={values.descrizione?.toUpperCase()}
								onChange={(e) => {
									const { name, value } = e.target;
									setFieldValue(name, value.toUpperCase());
								}}
								isInvalid={!!errors.descrizione && touched.descrizione}
							/>
							{errors.descrizione && touched.descrizione && (
								<Form.Control.Feedback className="ms-3" type="invalid">
									{errors.descrizione}
								</Form.Control.Feedback>
							)}

							{values.codiceAteco?.startsWith("92") ? (
								<div>
									<BftBooleanButton
										label="Sei un centro scommesse?"
										name="centroScommesse"
										value={String(values.centroScommesse)}
										onChange={handleChangeCustom("centroScommesse")}
										disabled
									/>
									{errors.centroScommesse && touched.centroScommesse && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors.centroScommesse}
										</Form.Control.Feedback>
									)}

									{values?.centroScommesse && (
										<Row>
											<Col md={6}>
												<div className="d-flex mt-4 mb-3 container d-flex justify-content-center">
													<h6>TULPS Gioco*</h6>
													<BftInfoTooltip
														className="ms-3"
														message="Allegare l'autorizzazione ricevuta dalla Questura per l'esercizio del Gioco"
													/>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.TULPS_GIOCO}
													trascinaDocumento={IdTipoDocumentiEstesi?.TULPS_GIOCO}
													file={values.SCO_TGI}
													onChange={(e) => handleFileChange(IdTipoDocumenti.TULPS_GIOCO, e)}
													isInvalid={touched.SCO_TGI && !!errors.SCO_TGI}
												/>
												{/* <div className="d-flex mt-4 mb-3 container d-flex justify-content-center">
													<h6>Licenza AAMS*</h6>
													<BftInfoTooltip
														className="ms-3"
														message="Allegare l'autorizzazione concessa dalla ADM (Agenzia dei Monopoli) al vostro Provider per l'esercizio della attività di scommesse"
													/>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.LICENZA_AAMS}
													file={values.SCO_LAA}
													onChange={(e) => handleFileChange(IdTipoDocumenti.LICENZA_AAMS, e)}
													isInvalid={!!(touched.SCO_LAA && errors.SCO_LAA)}
												/> */}
											</Col>
											{/* <Col md={6}>
												<div className="d-flex mt-4 mb-3 container d-flex justify-content-center">
													<h6>Delega casa madre*</h6>
													<BftInfoTooltip
														className="ms-3"
														message="Allegare la concessione con cui il Provider vi ha assegnato la licenza di Gioco"
													/>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.DELEGA_CASA_MADRE}
													file={values.SCO_DCM}
													onChange={(e) => handleFileChange(IdTipoDocumenti.DELEGA_CASA_MADRE, e)}
													isInvalid={!!(touched.SCO_DCM && errors.SCO_DCM)}
												/>
											</Col> */}
										</Row>
									)}
								</div>
							) : null}

							{values.codiceAteco?.startsWith("95.25") ||
							values.codiceAteco?.startsWith("66.19") ? (
								<Row>
									<Col md={6}>
										<div className="d-flex mt-4 mb-3 container justify-content-center">
											<h6>Licenza autorizzativa*</h6>
											<BftInfoTooltip className="ms-3" message="Lorem" />
										</div>
										<BftDropzone
											label=""
											idTipoDocumento={IdTipoDocumenti.LICENZA_AUTORIZZATIVA}
											trascinaDocumento={IdTipoDocumentiEstesi?.LICENZA_AUTORIZZATIVA}
											file={values.LIC_AUT}
											onChange={(e) => handleFileChange(IdTipoDocumenti.LICENZA_AUTORIZZATIVA, e)}
											isInvalid={!!(touched.LIC_AUT && errors.LIC_AUT)}
										/>
									</Col>
								</Row>
							) : null}

							{values.codiceSae === "500" ||
							values.codiceSae === "501" ||
							values?.naturaGiuridica == "Associazione" ||
							values?.naturaGiuridica == "Associazione In Partecipazione" ? (
								<div>
									<BftBooleanButton
										label="Sei un' associazione o circolo?"
										name="associazione"
										value={String(values.associazione)}
										onChange={handleChangeCustom("associazione")}
										disabled
									/>
									{errors.associazione && touched.associazione && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors.associazione}
										</Form.Control.Feedback>
									)}

									{values?.associazione && (
										<Row>
											<Col md={6}>
												<div className="d-flex mt-4 mb-3 container justify-content-center">
													<h6>Atto Costitutivo e/o statuto*</h6>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.ATTO_COSTITUTIVO}
													trascinaDocumento={IdTipoDocumentiEstesi?.ATTO_COSTITUTIVO}
													file={values.ASS_ATC}
													onChange={(e) => handleFileChange(IdTipoDocumenti.ATTO_COSTITUTIVO, e)}
													isInvalid={!!(touched.ASS_ATC && errors.ASS_ATC)}
												/>

												<div className="mt-4 mb-3 container d-flex justify-content-center">
													<h6>Ultimo verbale di nomina rapp. legale</h6>
													<BftInfoTooltip
														className="ms-3"
														message="Allegare il documento solo nel caso in cui il Legale Rappresentante nel corso del tempo sia stato sostituito a quello indicato nell'atto costitutivo allegato."
													/>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.VERBALE_RAPP_LEGALE}
													trascinaDocumento={IdTipoDocumentiEstesi?.VERBALE_RAPP_LEGALE}
													file={values.ASS_VRL}
													onChange={(e) => handleFileChange(IdTipoDocumenti.VERBALE_RAPP_LEGALE, e)}
													//isInvalid={!!(touched.ASS_VRL && errors.ASS_VRL)}
												/>
											</Col>
											<Col md={6}>
												<div className="d-flex mt-4 mb-3 container d-flex justify-content-center">
													<h6>Attribuzione partita iva*</h6>
												</div>
												<BftDropzone
													label=""
													idTipoDocumento={IdTipoDocumenti.ATTRIBUZIONE_PARTITA_IVA}
													trascinaDocumento={IdTipoDocumentiEstesi?.ATTRIBUZIONE_PARTITA_IVA}
													file={values.ASS_API}
													onChange={(e) =>
														handleFileChange(IdTipoDocumenti.ATTRIBUZIONE_PARTITA_IVA, e)
													}
													isInvalid={!!(touched.ASS_API && errors.ASS_API)}
												/>
											</Col>
										</Row>
									)}
								</div>
							) : null}

							<div className="mx-2 my-4">
								<BftButton type="submit" className="btn-buffetti col-12">
									Avanti
								</BftButton>
							</div>
						</form>
					);
				}}
			</Formik>
			<MessageModal
				show={showModal}
				message={
					<div>
						L’ATECO è la nomenclatura delle attività economiche grazie alla quale è possibile
						stabilire la categoria di pertinenza di ciascuna attività imprenditoriale ai fini
						fiscali e statistici. La classificazione attualmente in vigore è ATECO 2007
						aggiornamento nell'anno 2022. Per questo se la vostra attività è stata avviata in
						quell'anno o negli anni precedenti,{" "}
						<b>VI CHIEDIAMO DI INSERIRE IL VALORE DEL CODICE AGGIORNATO.</b>{" "}
						<a
							href="https://www.istat.it/it/archivio/265145"
							style={{ cursor: "pointer" }}
							target="_blank"
						>
							L'aggiornamento è scaricabile dal sito dell’Istat.{" "}
						</a>
					</div>
				}
				onClose={handleCloseModal}
				title="Convenzioni Codice Ateco"
			/>
		</div>
	);
};

export default DatiAziendali;
